// International phone input: a country-code dropdown + a local number field that
// together produce an E.164 value (+<dial><number>). Defaults to Tanzania.
// The `value`/`onChange` contract is the full E.164 string, so it drops into any
// form that already stores a phone string.
import React, { useMemo, useState } from 'react';

export interface PhoneCountry { iso: string; name: string; dial: string; }

// East Africa first, then a useful international set. Add more as needed.
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { iso: 'TZ', name: 'Tanzania', dial: '255' },
  { iso: 'KE', name: 'Kenya', dial: '254' },
  { iso: 'UG', name: 'Uganda', dial: '256' },
  { iso: 'RW', name: 'Rwanda', dial: '250' },
  { iso: 'BI', name: 'Burundi', dial: '257' },
  { iso: 'SS', name: 'South Sudan', dial: '211' },
  { iso: 'ET', name: 'Ethiopia', dial: '251' },
  { iso: 'CD', name: 'DR Congo', dial: '243' },
  { iso: 'ZM', name: 'Zambia', dial: '260' },
  { iso: 'MW', name: 'Malawi', dial: '265' },
  { iso: 'MZ', name: 'Mozambique', dial: '258' },
  { iso: 'ZA', name: 'South Africa', dial: '27' },
  { iso: 'NG', name: 'Nigeria', dial: '234' },
  { iso: 'GH', name: 'Ghana', dial: '233' },
  { iso: 'EG', name: 'Egypt', dial: '20' },
  { iso: 'AE', name: 'UAE', dial: '971' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '966' },
  { iso: 'IN', name: 'India', dial: '91' },
  { iso: 'GB', name: 'United Kingdom', dial: '44' },
  { iso: 'US', name: 'United States', dial: '1' },
  { iso: 'DE', name: 'Germany', dial: '49' },
  { iso: 'FR', name: 'France', dial: '33' },
  { iso: 'CN', name: 'China', dial: '86' },
  { iso: 'AU', name: 'Australia', dial: '61' },
];

// Longest dial code first so "+255…" matches TZ before a shorter code.
const BY_DIAL = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

function parse(value: string): { iso: string; local: string } {
  const v = (value || '').replace(/[^\d+]/g, '');
  if (v.startsWith('+')) {
    const digits = v.slice(1);
    const c = BY_DIAL.find((x) => digits.startsWith(x.dial));
    if (c) return { iso: c.iso, local: digits.slice(c.dial.length) };
  }
  return { iso: 'TZ', local: v.replace(/^\+?255/, '').replace(/\D/g, '') };
}

export const PhoneInput: React.FC<{
  value: string;
  onChange: (e164: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, disabled, id, placeholder = '712 345 678', className }) => {
  const { iso: derivedIso, local } = useMemo(() => parse(value), [value]);
  // The country choice must survive an EMPTY number: deriving it purely from
  // the E.164 string snapped the select back to TZ the moment you picked a
  // country before typing (emit('') → parse('') → default). The explicit pick
  // wins while there's no dial code in the value to disagree with it.
  const [pickedIso, setPickedIso] = useState<string | null>(null);
  const iso = value ? derivedIso : (pickedIso || derivedIso);
  const country = PHONE_COUNTRIES.find((c) => c.iso === iso) || PHONE_COUNTRIES[0];

  const emit = (nextIso: string, nextLocal: string) => {
    const c = PHONE_COUNTRIES.find((x) => x.iso === nextIso) || country;
    // Drop a local trunk leading zero (e.g. 0712… → 712…) so it becomes +255712…
    const digits = nextLocal.replace(/\D/g, '').replace(/^0+/, '');
    onChange(digits ? `+${c.dial}${digits}` : '');
  };

  return (
    <div className={`flex ${className || ''}`}>
      <select
        aria-label="Country code"
        value={iso}
        disabled={disabled}
        onChange={(e) => { setPickedIso(e.target.value); emit(e.target.value, local); }}
        className="rounded-l-lg border border-r-0 border-border bg-muted text-foreground text-sm px-2 py-2 outline-none focus:border-orange-400 disabled:opacity-60"
      >
        {PHONE_COUNTRIES.map((c) => (
          <option key={c.iso} value={c.iso}>{c.iso} +{c.dial}</option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        value={local}
        disabled={disabled}
        onChange={(e) => emit(iso, e.target.value)}
        placeholder={placeholder}
        className="flex-1 min-w-0 rounded-r-lg border border-border bg-background text-foreground text-sm px-3 py-2 outline-none focus:border-orange-400 disabled:bg-muted disabled:text-muted-foreground"
      />
    </div>
  );
};

export default PhoneInput;
